import { useRollbar } from '@rollbar/react'
import * as pageActions from 'client/actions/pageActions'

import { useWebsiteSettings } from 'client/store'
import { AccessDenied } from 'common/errors/AccessDenied'
import { BadRequest } from 'common/errors/BadRequest'
import { NetworkError } from 'common/errors/NetworkError'
import { NotFound } from 'common/errors/NotFound'
import { PageValidationError } from 'common/errors/PageValidationError'
import { PageValidationErrorNew } from 'common/errors/PageValidationErrorNew'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { useDispatch } from 'react-redux'
import { saveWebsitePage } from 'src/editor/api/website-pages-api'
import { useWebsitePageSavePayload } from 'src/editor/hooks/use-website-page-save-payload'
import { useWebsiteSave } from 'src/editor/hooks/use-website-save'
import { SaveIcon } from 'src/editor/shared/icons/save-icon'
import {
  DialogWindow,
  DialogWindowIconThemeEnum,
} from 'src/editor/shared/ui-lib/modal-window/dialog-window/dialog-window'
import { getWebsiteId } from 'src/editor/store/website-settings/website-settings-selector'

export interface UnsavedPageModalProps {
  isVisible: boolean
  onClose: () => void
  fetchPageData: () => Promise<void>
}
export const UnsavedPageModal = ({
  isVisible,
  onClose,
  fetchPageData,
}: UnsavedPageModalProps) => {
  const { t } = useTypedTranslation()
  const rollbar = useRollbar()
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const pageSavePayload = useWebsitePageSavePayload()
  const websiteId = useWebsiteSettings(getWebsiteId)
  const saveWebsiteData = useWebsiteSave()

  async function handleSave() {
    setIsLoading(true)
    try {
      if (websiteId && pageSavePayload) {
        await saveWebsiteData(websiteId)
        await saveWebsitePage(pageSavePayload)
      }
    } catch (e) {
      if (e instanceof PageValidationError) {
        dispatch(pageActions.pageValidationFail(e.messages))
      } else if (e instanceof PageValidationErrorNew) {
        toast.error(e.message)
      } else if (e instanceof NotFound) {
        dispatch(pageActions.savePageFail('core.errors.page_not_found'))
      } else if (e instanceof AccessDenied) {
        // do nothing
      } else if (e instanceof NetworkError) {
        // just show warning about data loss
        dispatch(pageActions.savePageFail('core.errors.save'))
      } else if (e instanceof BadRequest) {
        dispatch(pageActions.pageValidationFail(e.response.data))
      } else {
        dispatch(pageActions.savePageFail('core.errors.save'))
      }
    }
    setIsLoading(false)
  }

  const save = async () => {
    await handleSave()
    onClose()
    fetchPageData()
  }
  const onCancel = async () => {
    onClose()
    fetchPageData()
  }

  return (
    <DialogWindow
      isOpened={isVisible}
      onClose={onClose}
      title={t('website_settings.pages_management.unsaved_modal.title')}
      description={t(
        'website_settings.pages_management.unsaved_modal.description',
      )}
      iconTheme={DialogWindowIconThemeEnum.Confirmation}
      icon={<SaveIcon />}
      cancelButtonProps={{
        children: t('global.cancel'),
        onClick: onCancel,
      }}
      confirmButtonProps={{
        isLoading,
        onClick: save,
        children: t(
          'website_settings.pages_management.unsaved_modal.save_button',
        ),
      }}
    />
  )
}
